/**
 * Shared Firestore collection references and type-safe converters.
 *
 * Every collection path is defined once here so the rest of the app
 * never hardcodes Firestore paths directly.
 */

import {
  collection,
  doc,
  getDocs,
  query,
  where,
  orderBy,
  type CollectionReference,
  type DocumentReference,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
  type SnapshotOptions,
} from 'firebase/firestore'
import { db } from '../config'
import { normalizeProviderClinicFields } from '../../shared/providerClinics'
import type { Organization, UserProfile, Patient, Provider, Clinic, Invite, AppointmentDayRun, Appointment } from '../types'

// --- Generic converter factory ---
// Strips the `id` field on write (Firestore uses doc ID) and injects it on read.

function createConverter<T extends { id: string }>(): FirestoreDataConverter<T> {
  return {
    toFirestore(data: T) {
      const { id: _, ...rest } = data
      void _
      return rest
    },
    fromFirestore(snap: QueryDocumentSnapshot, options: SnapshotOptions): T {
      const data = snap.data(options)
      return { id: snap.id, ...data } as T
    },
  }
}

// --- Converters ---

export const orgConverter = createConverter<Organization>()
export const userProfileConverter = createConverter<UserProfile & { id: string }>()
export const patientConverter = createConverter<Patient>()

// Providers need a read-time normalization on top of the generic converter:
// pre-#98 docs carry `clinicId` + `isDefault` instead of the array fields.
// The mapping is shared with the functions tier (loadProviders) so the two
// can't drift — see shared/providerClinics.ts.
export const providerConverter: FirestoreDataConverter<Provider> = {
  toFirestore(data: Provider) {
    const { id: _, hasClinicArrays: __, ...rest } = data
    void _
    void __
    return rest
  },
  fromFirestore(snap: QueryDocumentSnapshot, options: SnapshotOptions): Provider {
    const data = snap.data(options)
    return {
      id: snap.id,
      ...data,
      ...normalizeProviderClinicFields(data),
      // Normalization erases the stored shape; record it so write paths can
      // tell a truly array-shaped doc from a legacy doc whose arrays were
      // synthesized on read (see Provider.hasClinicArrays).
      hasClinicArrays: Array.isArray(data.clinicIds),
    } as Provider
  },
}

// --- Top-level collection refs ---

export function orgRef(orgId: string): DocumentReference<Organization> {
  return doc(db, 'organizations', orgId).withConverter(orgConverter)
}

export function orgsCollection(): CollectionReference<Organization> {
  return collection(db, 'organizations').withConverter(orgConverter)
}

export function userProfileRef(uid: string): DocumentReference<UserProfile & { id: string }> {
  return doc(db, 'users', uid).withConverter(userProfileConverter)
}

// --- Org-scoped sub-collection refs ---

export function patientsCollection(orgId: string): CollectionReference<Patient> {
  return collection(db, 'organizations', orgId, 'patients').withConverter(patientConverter)
}

export function patientRef(orgId: string, patientId: string): DocumentReference<Patient> {
  return doc(db, 'organizations', orgId, 'patients', patientId).withConverter(patientConverter)
}

export function providersCollection(orgId: string): CollectionReference<Provider> {
  return collection(db, 'organizations', orgId, 'providers').withConverter(providerConverter)
}

export function providerRef(orgId: string, providerId: string): DocumentReference<Provider> {
  return doc(db, 'organizations', orgId, 'providers', providerId).withConverter(providerConverter)
}

export function eligibilityChecksCollection(orgId: string, patientId: string) {
  return collection(db, 'organizations', orgId, 'patients', patientId, 'eligibilityChecks')
}

/** Top-level eligibility checks (where the cloud function writes directly). */
export function orgEligibilityChecksCollection(orgId: string) {
  return collection(db, 'organizations', orgId, 'eligibilityChecks')
}

// --- SMS follow-up (Recovery Loop) — backend-written, client read-only ---

/** Org-scoped SMS conversations (one per patient phone). */
export function orgSmsConversationsCollection(orgId: string) {
  return collection(db, 'organizations', orgId, 'smsConversations')
}

/** Messages within one conversation, keyed by Telnyx message id. */
export function smsMessagesCollection(orgId: string, phone: string) {
  return collection(db, 'organizations', orgId, 'smsConversations', phone, 'messages')
}

/** One conversation thread (doc id = the patient's +E.164 phone). */
export function smsConversationRef(orgId: string, phone: string) {
  return doc(db, 'organizations', orgId, 'smsConversations', phone)
}

/** One recovery case under a conversation (doc id = the failed check's id). */
export function smsCaseRef(orgId: string, phone: string, checkId: string) {
  return doc(db, 'organizations', orgId, 'smsConversations', phone, 'cases', checkId)
}

// --- Clinic refs ---

export const clinicConverter = createConverter<Clinic>()

export function clinicsCollection(orgId: string): CollectionReference<Clinic> {
  return collection(db, 'organizations', orgId, 'clinics').withConverter(clinicConverter)
}

export function clinicRef(orgId: string, clinicId: string): DocumentReference<Clinic> {
  return doc(db, 'organizations', orgId, 'clinics', clinicId).withConverter(clinicConverter)
}

// --- Appointment day runs ---

export const appointmentDayRunConverter = createConverter<AppointmentDayRun>()

export function appointmentDayRunsCollection(orgId: string): CollectionReference<AppointmentDayRun> {
  return collection(db, 'organizations', orgId, 'appointmentDayRuns').withConverter(appointmentDayRunConverter)
}

/** No converter — patient subdocs are manually mapped in fetchRunPatients()
 *  (src/services/appointments.ts) because appointmentDate and clinicId
 *  come from the parent run, not the subdoc. */
export function appointmentRunPatientsCollection(orgId: string, runId: string) {
  return collection(db, 'organizations', orgId, 'appointmentDayRuns', runId, 'patients')
}

// --- Multi-source appointments ---
// Clinic-scoped subcollection deduped by identity hash (see
// shared/appointmentIdentity.ts). Replaces the legacy
// appointmentDayRuns/{runId}/patients/{hash} subdocs.

export const appointmentConverter = createConverter<Appointment>()

export function appointmentsCollection(
  orgId: string,
  clinicId: string,
): CollectionReference<Appointment> {
  return collection(
    db, 'organizations', orgId, 'clinics', clinicId, 'appointments',
  ).withConverter(appointmentConverter)
}

export function appointmentRef(
  orgId: string,
  clinicId: string,
  appointmentId: string,
): DocumentReference<Appointment> {
  return doc(
    db, 'organizations', orgId, 'clinics', clinicId, 'appointments', appointmentId,
  ).withConverter(appointmentConverter)
}

// --- RBAC support collections (managed by cloud functions, read-only on client) ---

export function auditLogsCollection(orgId: string) {
  return collection(db, 'organizations', orgId, 'auditLogs')
}

export function invitesCollection(orgId: string) {
  return collection(db, 'organizations', orgId, 'invites')
}

/**
 * List all non-accepted invites for an org, newest first.
 *
 * Excludes `accepted` invites — those users already appear as real OrgUser rows.
 * `tokenHash` is stripped before returning; it must never reach the client.
 */
export async function listOrgInvites(orgId: string): Promise<Invite[]> {
  const q = query(
    invitesCollection(orgId),
    where('status', '!=', 'accepted'),
    orderBy('status'),          // required when using != filter (Firestore composite index rule)
    orderBy('createdAt', 'desc'),
  )
  const snap = await getDocs(q)
  return snap.docs.map((d) => {
    const data = d.data() as Record<string, unknown>
    const { tokenHash: _, ...rest } = data
    return { id: d.id, ...rest } as Invite
  })
}
